import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import { ApiError, earningsBonusesService } from '../services';

const DEFAULT_SHOPPER = {
  id: '3fe908619dAe3782b',
  firstName: 'John',
  lastName: 'Doe',
};

const INITIAL_FILTERS = {
  merchantName: '',
  period: 'all',
  channel: '',
  type: '',
  dateFrom: '',
  dateTo: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  pageCount: 1,
  size: 20,
  count: 0,
  total: 0,
};

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channel' },
  { value: '1Snap', label: '1Snap' },
  { value: '1Tap', label: '1Tap' },
  { value: 'Manual Transaction', label: 'Manual Transaction' },
];

const TRANSACTION_TYPE_OPTIONS = [
  { value: '', label: 'All Type' },
  { value: 'Cashback Merchant', label: 'Cashback Merchant' },
  { value: 'Cashback E&E', label: 'Cashback E&E' },
  { value: 'Withdrawal', label: 'Withdrawal' },
];

const safeText = value => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const formatDateTime = value => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatCurrencyEUR = (value, maximumFractionDigits = 2) => {
  if (value === null || value === undefined || value === '') return '-';

  const number = Number(value);
  if (!Number.isFinite(number)) return '-';

  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(number);
};

const formatPercentage = value => {
  if (value === null || value === undefined || value === '') return '-';

  const number = Number(value);
  if (!Number.isFinite(number)) return '-';

  return `${number.toFixed(2)}%`;
};

const getResponseMessage = response =>
  response?.data?.message ||
  response?.message ||
  'Your export is being processed. The file will be sent to your registered email address shortly.';

const getPageNumbers = pagination =>
  Array.from(
    new Set(
      [
        1,
        pagination.page - 1,
        pagination.page,
        pagination.page + 1,
        pagination.pageCount,
      ].filter(page => page >= 1 && page <= pagination.pageCount)
    )
  );

const ShopperEarningsBonusesPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const shopper = useMemo(() => {
    const stateShopper = location.state?.shopper || {};

    return {
      ...DEFAULT_SHOPPER,
      ...stateShopper,
      id: stateShopper.id || id || DEFAULT_SHOPPER.id,
    };
  }, [id, location.state]);

  const fullName =
    shopper.fullName ||
    [shopper.firstName, shopper.lastName].filter(Boolean).join(' ') ||
    'Shopper';

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);
  const [searchFocused, setSearchFocused] = useState(false);
  const [shoppingCycles, setShoppingCycles] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const messageBannerRef = useRef(null);

  const displayStart =
    pagination.count > 0 ? (pagination.page - 1) * pagination.size + 1 : 0;
  const displayEnd =
    pagination.count > 0
      ? Math.min(pagination.page * pagination.size, pagination.count)
      : 0;
  const pageNumbers = getPageNumbers(pagination);

  const buildParams = useCallback(
    (sourceFilters, page = 1, includePagination = true) => {
      const params = {
        keyword: sourceFilters.merchantName,
        shoppingCycleCode: sourceFilters.period,
        dateFrom: sourceFilters.dateFrom,
        dateTo: sourceFilters.dateTo,
        channel: sourceFilters.channel,
        type: sourceFilters.type,
      };

      if (includePagination) {
        params.page = page;
        params.size = pagination.size;
      }

      return params;
    },
    [pagination.size]
  );

  const fetchTransactions = useCallback(
    async ({ page = 1, sourceFilters = appliedFilters } = {}) => {
      setLoading(true);
      setError(null);

      try {
        const params = buildParams(sourceFilters, page);
        const response = await earningsBonusesService.getShopperEarnings(
          shopper.id,
          params
        );
        const data = response?.data || {};
        const rows = Array.isArray(data.data) ? data.data : [];

        setTransactions(rows);
        setPagination({
          page: data.page || page,
          pageCount: data.pageCount || 1,
          size: data.size || pagination.size,
          count: data.count || 0,
          total: data.total || 0,
        });
      } catch (err) {
        console.error('Failed to fetch shopper earnings:', err);
        setTransactions([]);
        setPagination(prev => ({
          ...prev,
          page,
          pageCount: 1,
          count: 0,
          total: 0,
        }));

        if (err instanceof ApiError) {
          setError(`Failed to load earnings: ${err.message}`);
        } else {
          setError('Failed to load earnings. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [appliedFilters, buildParams, pagination.size, shopper.id]
  );

  const loadShoppingCycles = useCallback(async () => {
    try {
      const response = await earningsBonusesService.getShoppingCycles(4);
      setShoppingCycles(response.data || []);
    } catch (err) {
      console.warn('Failed to load shopping cycle options:', err);
    }
  }, []);

  useEffect(() => {
    loadShoppingCycles();
  }, [loadShoppingCycles]);

  useEffect(() => {
    fetchTransactions({ page: 1, sourceFilters: appliedFilters });
  }, [appliedFilters, fetchTransactions]);

  useEffect(() => {
    if ((error || exportResult) && messageBannerRef.current) {
      messageBannerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [error, exportResult]);

  const handleFilterChange = field => event => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handlePeriodChange = event => {
    setFilters(prev => ({
      ...prev,
      period: event.target.value,
      dateFrom: '',
      dateTo: '',
    }));
  };

  const handleDateRangeChange = useCallback(({ startDate, endDate }) => {
    setFilters(prev => ({
      ...prev,
      period: startDate || endDate ? 'all' : prev.period,
      dateFrom: startDate || '',
      dateTo: endDate || '',
    }));
  }, []);

  const clearFilters = () => {
    setExportResult(null);
    setFilters({ ...INITIAL_FILTERS });
    setAppliedFilters({ ...INITIAL_FILTERS });
  };

  const applyFilters = () => {
    setExportResult(null);
    setAppliedFilters({ ...filters });
  };

  const handlePageChange = newPage => {
    if (
      loading ||
      newPage < 1 ||
      newPage > pagination.pageCount ||
      newPage === pagination.page
    ) {
      return;
    }

    fetchTransactions({
      page: newPage,
      sourceFilters: appliedFilters,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    setExportResult(null);

    try {
      const params = buildParams(appliedFilters, 1, false);
      const response = await earningsBonusesService.exportShopperEarnings(
        shopper.id,
        params
      );

      setExportResult({ message: getResponseMessage(response) });
    } catch (err) {
      console.error('Failed to export shopper earnings:', err);
      if (err instanceof ApiError) {
        setError(`Failed to export earnings: ${err.message}`);
      } else {
        setError('Failed to export earnings. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  const goBack = () => {
    navigate(ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: shopper.id }), {
      state: { shopper },
    });
  };

  const BackIcon = ({ className = 'w-5 h-5' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path strokeLinecap='round' strokeLinejoin='round' d='M15 19l-7-7 7-7' />
      <path strokeLinecap='round' strokeLinejoin='round' d='M8 12h13' />
    </svg>
  );

  const SearchIcon = ({ className = 'w-5 h-5' }) => (
    <svg
      className={className}
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        d='M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z'
      />
    </svg>
  );

  const SelectField = ({ id: fieldId, label, value, onChange, children }) => (
    <div>
      <label
        htmlFor={fieldId}
        className='block text-sm font-medium text-gray-900 mb-2'
      >
        {label}
      </label>
      <select
        id={fieldId}
        value={value}
        onChange={onChange}
        disabled={loading}
        className='block w-full px-3 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
      >
        {children}
      </select>
    </div>
  );

  const DateRangeField = () => (
    <DateRangePicker
      startDate={filters.dateFrom}
      endDate={filters.dateTo}
      onDateRangeChange={handleDateRangeChange}
      disabled={loading}
      placeholder='Select date range'
      label={{
        start: 'Transaction Date From',
        end: 'Transaction Date To',
      }}
      className='xl:col-span-3'
    />
  );

  const ShoppingCycleOptions = () => (
    <>
      <option value='all'>All Period</option>
      <option value='current'>Current Cycle</option>
      {shoppingCycles.slice(1).map(cycle => (
        <option key={cycle.id || cycle.code} value={cycle.code}>
          {cycle.name || cycle.code}
        </option>
      ))}
    </>
  );

  const renderPagination = () => {
    if (pagination.count === 0) return null;

    return (
      <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
        <p className='text-sm text-gray-700'>
          Page <span className='font-medium'>{pagination.page}</span> of{' '}
          <span className='font-medium'>{pagination.pageCount}</span>
        </p>
        <nav
          className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
          aria-label='Pagination'
        >
          <button
            type='button'
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={loading || pagination.page <= 1}
            className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
          >
            <span className='sr-only'>Previous</span>
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </button>
          {pageNumbers.map(num => (
            <button
              key={num}
              type='button'
              onClick={() => handlePageChange(num)}
              disabled={loading}
              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                num === pagination.page
                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
              } disabled:opacity-50`}
            >
              {num}
            </button>
          ))}
          <button
            type='button'
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={loading || pagination.page >= pagination.pageCount}
            className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
          >
            <span className='sr-only'>Next</span>
            <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                clipRule='evenodd'
              />
            </svg>
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className='space-y-6'>
      <button
        type='button'
        onClick={goBack}
        className='inline-flex items-center gap-2 px-4 py-3 border border-gray-400 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
      >
        <BackIcon />
        Back
      </button>

      <div className='bg-white shadow-sm border-b border-gray-200 px-6 py-7'>
        <h1 className='text-3xl font-bold text-gray-900'>Earnings & Bonuses</h1>
        <p className='mt-2 text-base text-gray-500'>
          Displaying transaction earning & bonuses history information
        </p>
      </div>

      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 xl:items-end'>
          <div className='xl:col-span-3'>
            <label
              htmlFor='merchant-search'
              className='block text-sm font-medium text-gray-900 mb-2'
            >
              Search Merchant Name
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <SearchIcon
                  className={`w-5 h-5 ${
                    searchFocused ? 'text-blue-500' : 'text-gray-400'
                  }`}
                />
              </div>
              <input
                id='merchant-search'
                type='text'
                value={filters.merchantName}
                onChange={handleFilterChange('merchantName')}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder='Search by name (min 3 chars)...'
                disabled={loading}
                className='block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
              />
            </div>
          </div>

          <div className='xl:col-span-3'>
            <SelectField
              id='shopping-cycle-period'
              label='Shopping Cycle Period'
              value={filters.period}
              onChange={handlePeriodChange}
            >
              <ShoppingCycleOptions />
            </SelectField>
          </div>

          <div className='xl:col-span-3'>
            <SelectField
              id='transaction-channel'
              label='Transaction Channel'
              value={filters.channel}
              onChange={handleFilterChange('channel')}
            >
              {CHANNEL_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>

          <div className='xl:col-span-3'>
            <SelectField
              id='transaction-type'
              label='Transaction Type'
              value={filters.type}
              onChange={handleFilterChange('type')}
            >
              {TRANSACTION_TYPE_OPTIONS.map(option => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>

          <DateRangeField />

          <div className='xl:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <button
              type='button'
              onClick={clearFilters}
              disabled={loading}
              className='w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 disabled:opacity-60'
            >
              Clear Filters
            </button>
            <button
              type='button'
              onClick={applyFilters}
              disabled={loading}
              className='w-full px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60'
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error & Export Result Display */}
      {(error || exportResult) && (
        <div ref={messageBannerRef}>
          {error && (
            <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
              <div className='flex items-center'>
                <svg
                  className='w-5 h-5 text-red-400 mr-2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
                <p className='text-sm text-red-700'>{error}</p>
                <button
                  onClick={() => setError(null)}
                  className='ml-auto text-red-400 hover:text-red-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {exportResult && (
            <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
              <div className='flex items-start'>
                <svg
                  className='w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <div className='flex-1 min-w-0'>
                  <h4 className='text-sm font-semibold text-green-800 mb-1'>
                    Export Successful
                  </h4>
                  <p className='text-sm text-green-700'>
                    {exportResult.message}
                  </p>
                </div>
                <button
                  onClick={() => setExportResult(null)}
                  className='ml-auto text-green-400 hover:text-green-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
          <div>
            <h2 className='text-2xl font-bold text-gray-800 flex items-center gap-2'>
              {fullName}
              {loading && (
                <svg
                  className='animate-spin h-5 w-5 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h2>
            <p className='text-sm text-gray-500 mt-2 break-all'>
              Shopper ID: {shopper.id}
            </p>
            {pagination.count > 0 && (
              <p className='text-sm text-gray-500 mt-1'>
                Showing {displayStart} to {displayEnd} of {pagination.count}{' '}
                records
              </p>
            )}
          </div>
          <button
            type='button'
            onClick={handleExport}
            disabled={exporting || loading || transactions.length === 0}
            className='inline-flex justify-center px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 md:min-w-[180px] disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {exporting ? 'Exporting...' : 'Export Data'}
          </button>
        </div>

        <div className='overflow-x-auto border-t border-gray-200'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Date
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Merchant Name
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Status
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Updated
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Channel
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Type
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Transaction Amount
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Cashback Percentage
                </th>
                <th className='px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Cashback Amount Received
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {loading && transactions.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className='px-4 py-8 text-center text-sm text-gray-500'
                  >
                    Loading earnings...
                  </td>
                </tr>
              )}

              {!loading && transactions.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className='px-4 py-8 text-center text-sm text-gray-500'
                  >
                    No earnings found.
                  </td>
                </tr>
              )}

              {transactions.map(transaction => {
                const cashbackAmount = Number(
                  transaction.cashbackAmountReceived
                );
                const cashbackClassName =
                  Number.isFinite(cashbackAmount) && cashbackAmount < 0
                    ? 'text-red-500'
                    : 'text-gray-900';

                return (
                  <tr
                    key={transaction.id}
                    className='hover:bg-gray-50 align-top'
                  >
                    <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                      {formatDateTime(transaction.transactionDate)}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900 min-w-[180px]'>
                      {transaction.merchantName || 'Everywhere & Everyday'}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900'>
                      {safeText(transaction.status)}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                      {formatDateTime(transaction.lastUpdated)}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900 min-w-[130px]'>
                      {safeText(transaction.channel)}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900 min-w-[140px]'>
                      {safeText(transaction.transactionType)}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                      {formatCurrencyEUR(transaction.transactionAmount)}
                    </td>
                    <td className='px-4 py-4 text-sm text-gray-900 whitespace-nowrap'>
                      {formatPercentage(transaction.cashbackPercentage)}
                    </td>
                    <td
                      className={`px-4 py-4 text-sm whitespace-nowrap ${cashbackClassName}`}
                    >
                      {formatCurrencyEUR(transaction.cashbackAmountReceived, 8)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {renderPagination()}
      </div>
    </div>
  );
};

export default ShopperEarningsBonusesPage;
